import {
  actionRepos,
  actionsConfigs,
  clearActionsStores,
  initializeActionsPolling,
  refreshActionsData,
  updateActionsConfigs
} from '$features/actions/stores/actions.store';
import { clearConfigStores, type CombinedConfig } from '$features/config/stores/config.store';
import {
  clearPullRequestStores,
  pullRequestConfigs,
  pullRequestRepos,
  updatePullRequestConfigs
} from '$features/pull-requests/stores/pull-requests.store';
import { configService, type RepoConfig } from '$integrations/firebase';
import { setStorageObject } from '$shared/services/storage.service';
import {
  allPullRequests,
  allWorkflowJobs,
  allWorkflowRuns,
  clearAllStores as clearRepositoryStores,
  actionsConfigs as configActionsConfigs,
  pullRequestConfigs as configPullRequestConfigs,
  getCombinedConfigs,
  initializePullRequestsPolling,
  loadRepositoryConfigs,
  loadedPullRequestRepos,
  refreshConfigurations,
  refreshPullRequestsData,
  updateRepositoryConfigs
} from '$shared/stores/repository-service';

export class RepositoryFacade {
  private static instance: RepositoryFacade;

  private constructor() { }

  static getInstance(): RepositoryFacade {
    if (!RepositoryFacade.instance) {
      RepositoryFacade.instance = new RepositoryFacade();
    }
    return RepositoryFacade.instance;
  }

  async loadAllConfigurations(): Promise<void> {
    // Load configurations from Firebase/localStorage without triggering data fetching
    try {
      const configs = await configService.getConfigs();

      // Update stores with configs
      pullRequestConfigs.set(configs.pullRequests || []);
      actionsConfigs.set(configs.actions || []);

      // Update local storage
      setStorageObject('pull-requests-configs', configs.pullRequests || []);
      setStorageObject('actions-configs', configs.actions || []);

      // Now call our modified loadRepositoryConfigs to initialize empty data
      await loadRepositoryConfigs();

    } catch (error) {
      console.error('❌ Failed to load configurations:', error);
      throw error;
    }
  }

  async getCombinedConfigurations(): Promise<CombinedConfig[]> {
    return await getCombinedConfigs();
  }

  async updateConfigurations(configs: CombinedConfig[]): Promise<void> {
    await updateRepositoryConfigs(configs);
  }

  async refreshAllConfigurations(): Promise<void> {
    await refreshConfigurations();
  }

  initializePullRequestsPolling(configs: RepoConfig[]): void {
    initializePullRequestsPolling({ repoConfigs: configs });
  }

  async refreshPullRequestsData(configs: RepoConfig[]): Promise<void> {
    await refreshPullRequestsData(configs);
  }

  async updatePullRequestConfigurations(configs: RepoConfig[]): Promise<void> {
    await updatePullRequestConfigs(configs);
  }

  initializeActionsPolling(configs: RepoConfig[]): void {
    initializeActionsPolling(configs);
  }

  async refreshActionsData(configs: RepoConfig[]): Promise<void> {
    await refreshActionsData(configs);
  }

  async updateActionsConfigurations(configs: RepoConfig[]): Promise<void> {
    await updateActionsConfigs(configs);
  }

  getPullRequestsStore() {
    return allPullRequests;
  }

  getLoadedPullRequestReposStore() {
    return loadedPullRequestRepos;
  }

  getWorkflowRunsStore() {
    return allWorkflowRuns;
  }

  getWorkflowJobsStore() {
    return allWorkflowJobs;
  }

  getPullRequestConfigsStore() {
    return configPullRequestConfigs;
  }

  getActionsConfigsStore() {
    return configActionsConfigs;
  }

  getPullRequestReposStore() {
    return pullRequestRepos;
  }

  getActionReposStore() {
    return actionRepos;
  }

  getRepoKey(config: RepoConfig): string {
    return `${config.org}/${config.repo}`;
  }

  clearAllStores(): void {
    clearPullRequestStores();
    clearActionsStores();
    clearConfigStores();
  }

  /**
   * Clears everything, including the persisted dashboard snapshot. Used on an
   * explicit sign-out; automatic sign-outs keep the snapshot so the dashboard
   * can render from cache after re-authenticating.
   */
  clearAllData(): void {
    this.clearAllStores();
    clearRepositoryStores();
  }

  getAllStores() {
    return {
      pullRequests: allPullRequests,
      workflowRuns: allWorkflowRuns,
      workflowJobs: allWorkflowJobs,
      pullRequestConfigs,
      actionsConfigs,
      pullRequestRepos,
      actionRepos
    };
  }
}

// Export singleton instance
export const repositoryFacade = RepositoryFacade.getInstance();
